import React, { useEffect, useDeferredValue, useState } from 'react';
import { Customer } from '@/types';
import { api } from '@/services/api';
import { useCustomers, DataErrorBoundary, type CustomerListParams } from '@features/admin/data';
import { Search, ArrowDown, ArrowUp, ArrowUpDown, Loader2 } from 'lucide-react';
import { EmptyRow, Table, TBody, TD, TH, THead, TR } from '@shared/components/ui/Table';
import { Pagination } from '@shared/components/common/Pagination';
import { formatUkDate } from '@/lib/dates';

interface CustomerListProps {
  targetCustomerId: string | null;
  targetSearch: string | null;
  onSelect: (customer: Customer) => void;
  onClearTarget: () => void;
  onClearTargetSearch: () => void;
}

type SortState = { sortBy: 'registeredAt' | 'name'; sortDir: 'asc' | 'desc' };

export const CustomerList: React.FC<CustomerListProps> = ({
  targetCustomerId,
  targetSearch,
  onSelect,
  onClearTarget,
  onClearTargetSearch
}) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search);
  const [sort, setSort] = useState<SortState>({ sortBy: 'registeredAt', sortDir: 'desc' });

  // Navigated here to open a specific customer (e.g. clicking a candidate name in
  // Bookings): fetch them by id directly instead of hoping they're on the loaded page.
  useEffect(() => {
    if (!targetCustomerId) return;
    let cancelled = false;
    api
      .getStaffCustomer(targetCustomerId)
      .then((customer) => {
        if (!cancelled && customer?.id) onSelect(customer);
      })
      .catch(() => {
        // Customer no longer exists — stay on the list.
      })
      .finally(() => {
        if (!cancelled) onClearTarget();
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetCustomerId]);

  // Navigated here with a search seed (candidate has no linked customer record):
  // pre-fill the search box so the right records are already on screen.
  useEffect(() => {
    if (targetSearch == null) return;
    setSearch(targetSearch);
    onClearTargetSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetSearch]);

  useEffect(() => {
    setPage(1);
  }, [search, sort.sortBy, sort.sortDir]);

  const toggleNameSort = () => {
    setSort((prev) => {
      if (prev.sortBy !== 'name') return { sortBy: 'name', sortDir: 'asc' };
      if (prev.sortDir === 'asc') return { sortBy: 'name', sortDir: 'desc' };
      return { sortBy: 'registeredAt', sortDir: 'desc' };
    });
  };

  const nameSortIcon =
    sort.sortBy !== 'name' ? (
      <ArrowUpDown className="w-3.5 h-3.5 text-slate-400" />
    ) : sort.sortDir === 'asc' ? (
      <ArrowUp className="w-3.5 h-3.5 text-slate-600" />
    ) : (
      <ArrowDown className="w-3.5 h-3.5 text-slate-600" />
    );

  return (
    <>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-xl font-bold text-slate-800">Customer Database</h3>
          <p className="text-xs text-slate-500">
            Manage patient records and audit communication history.
          </p>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-2.5 text-slate-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search customers..."
            className="pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none w-64 bg-white text-sm"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <DataErrorBoundary invalidateOnRetry="customers">
        <React.Suspense
          fallback={
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 text-center text-slate-500">
              <Loader2 className="w-10 h-10 text-slate-200 animate-spin inline-block mr-2" />
              Loading customers...
            </div>
          }
        >
          <CustomerListInner
            page={page}
            pageSize={pageSize}
            search={deferredSearch}
            sortBy={sort.sortBy}
            sortDir={sort.sortDir}
            nameSortIcon={nameSortIcon}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
            onToggleNameSort={toggleNameSort}
            onSelect={(c) => {
              onSelect(c);
              onClearTarget();
            }}
          />
        </React.Suspense>
      </DataErrorBoundary>
    </>
  );
};

interface CustomerListInnerProps {
  page: number;
  pageSize: number;
  search: string;
  sortBy: 'registeredAt' | 'name';
  sortDir: 'asc' | 'desc';
  nameSortIcon: React.ReactNode;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onToggleNameSort: () => void;
  onSelect: (customer: Customer) => void;
}

const CustomerListInner: React.FC<CustomerListInnerProps> = ({
  page,
  pageSize,
  search,
  sortBy,
  sortDir,
  nameSortIcon,
  onPageChange,
  onPageSizeChange,
  onToggleNameSort,
  onSelect
}) => {
  const params: CustomerListParams = { page, pageSize, q: search || undefined, sortBy, sortDir };
  const result = useCustomers(params);
  const customers = result.items || [];
  const totalCount = Number(result.totalCount || 0);

  return (
    <>
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex-1 overflow-hidden">
        <div className="overflow-y-auto h-full">
          <Table className="w-full text-left">
            <THead className="bg-slate-50 border-b sticky top-0 z-10">
              <TR>
                <TH className="font-bold text-[10px] uppercase tracking-widest text-slate-500">
                  <button
                    type="button"
                    onClick={onToggleNameSort}
                    className="inline-flex items-center gap-1.5 hover:text-slate-800"
                    title="Toggle patient name sort"
                  >
                    Patient
                    {nameSortIcon}
                  </button>
                </TH>
                <TH className="font-bold text-[10px] uppercase tracking-widest text-slate-500">
                  Contact Details
                </TH>
                <TH className="font-bold text-[10px] uppercase tracking-widest text-slate-500">
                  Registration
                </TH>
                <TH
                  align="center"
                  className="font-bold text-[10px] uppercase tracking-widest text-slate-500"
                >
                  Visits
                </TH>
                <TH
                  align="right"
                  className="font-bold text-[10px] uppercase tracking-widest text-slate-500"
                >
                  Action
                </TH>
              </TR>
            </THead>
            <TBody className="divide-y divide-slate-100">
              {customers.length === 0 ? (
                <EmptyRow colSpan={5} className="p-8 text-center text-slate-500 italic">
                  No customers found matching search criteria.
                </EmptyRow>
              ) : (
                customers.map((c) => (
                  <TR key={c.id} className="hover:bg-slate-50 transition-colors group">
                    <TD className="font-bold text-slate-900">{c.name}</TD>
                    <TD className="text-sm text-slate-600">
                      <div className="flex items-center gap-2 font-medium">{c.email}</div>
                      <div className="text-[10px] text-slate-400 font-bold">{c.phone}</div>
                    </TD>
                    <TD className="text-sm text-slate-500 font-medium">
                      {formatUkDate(c.registeredAt, '—', { preserveTimeZone: true })}
                    </TD>
                    <TD align="center">
                      <span className="bg-slate-100 px-2.5 py-1 rounded-full text-[10px] font-black text-slate-600 border border-slate-200">
                        {c.visitCount ?? 0}
                      </span>
                    </TD>
                    <TD align="right">
                      <button
                        onClick={() => onSelect(c)}
                        className="bg-white border border-slate-200 text-slate-700 px-4 py-1.5 rounded-lg text-xs font-bold hover:bg-teal-600 hover:text-white hover:border-teal-600 transition-all shadow-sm"
                      >
                        Manage Record
                      </button>
                    </TD>
                  </TR>
                ))
              )}
            </TBody>
          </Table>
        </div>
      </div>
      <Pagination
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </>
  );
};
